import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useSite } from '../context/SiteContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { IconArrow, IconMapPin, IconClock } from '../components/Icons';
import styles from './TripDetails.module.css';

// ─── CAROUSEL ────────────────────────────────────────────────────────────────
function Carousel({ images, title }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const prev = () => setIdx(i => (i - 1 + images.length) % images.length);
  const next = () => setIdx(i => (i + 1) % images.length);

  useEffect(() => {
    if (images.length <= 1 || paused) return;
    const t = setTimeout(next, 4500);
    return () => clearTimeout(t);
  });

  return (
    <div
      className={styles.swiperOuter}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className={styles.swiper}>
        {images.map((img, i) => (
          <div
            key={i}
            className={`${styles.slide} ${i === idx ? styles.slideActive : ''}`}
          >
            <img
              src={img.url}
              alt={`${title} – ${i + 1}`}
              className={styles.slideImg}
              loading={i === 0 ? 'eager' : 'lazy'}
            />
            <div className={styles.slideOverlay} />
          </div>
        ))}
      </div>

      {images.length > 1 && (
        <>
          <button className={`${styles.navBtn} ${styles.navPrev}`} onClick={prev} aria-label="Previous">
            <IconArrow size={20} style={{ transform: 'rotate(180deg)' }} />
          </button>
          <button className={`${styles.navBtn} ${styles.navNext}`} onClick={next} aria-label="Next">
            <IconArrow size={20} />
          </button>
          <div className={styles.pagination}>
            {images.map((_, i) => (
              <button
                key={i}
                className={`${styles.dot} ${i === idx ? styles.dotActive : ''}`}
                onClick={() => setIdx(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
          <div className={styles.photoCounter}>📷 {images.length} photos</div>
        </>
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function TripDetails() {
  const { slug } = useParams();
  const { data } = useSite();
  const navigate = useNavigate();

  const trip = data.trips.find(t => t.slug === slug);

  if (!trip) {
    return (
      <>
        <SEO
          title="Trip Not Found | Keepers Experience"
          description="The trip you're looking for is not available. Explore other hiking trips in Jordan."
          noindex={true}
        />
        <div className={styles.page}>
          <Navbar />
          <div className={styles.notFound}>
            <div className={styles.notFoundIcon}>🌿</div>
            <h2>Trip not found</h2>
            <button className={styles.backBtn} onClick={() => navigate('/trips')}>
              <span className={styles.backArrow}><IconArrow size={14} /></span> Back to Trips
            </button>
          </div>
        </div>
      </>
    );
  }

  // Sort images: main first
  const images = trip.images && trip.images.length > 0
    ? [...trip.images].sort((a, b) => (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0))
    : trip.image
      ? [{ url: trip.image, isMain: true }]
      : [];

  const categoryColors = {
    hiking: { bg: '#e6f4ea', color: '#2d7a3e' },
    walking: { bg: '#e8f0fe', color: '#1a56c4' },
    activity: { bg: '#fff3e0', color: '#e65100' },
  };
  const catObj = (data.categories || []).find(c => c.id === trip.category);
  const cat = catObj ? { bg: catObj.bg, color: catObj.color } : (categoryColors[trip.category] || categoryColors.hiking);

  const tripDescription = trip.description || trip.highlights?.[0] || 'Join us on an unforgettable hiking adventure in Jordan';
  const mainImage = images.length > 0 ? images[0].url : 'https://keepersexperience.com/full_logo.png';

  return (
    <>
      <SEO
        title={`${trip.title} | Hiking in Jordan - Keepers Experience`}
        description={tripDescription.substring(0, 160)}
        image={mainImage}
        url={`https://keepersexperience.com/trips/${trip.slug}`}
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "TouristAttraction",
          "name": trip.title,
          "description": tripDescription,
          "image": mainImage,
          "url": `https://keepersexperience.com/trips/${trip.slug}`,
          "location": {
            "@type": "Place",
            "name": trip.location
          },
          "address": {
            "@type": "PostalAddress",
            "addressLocality": trip.location,
            "addressCountry": "JO"
          }
        }}
      />
      <div className={styles.page}>
      <Navbar />

      {/* ─── BACK ─── */}
      <div className={styles.topBar}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <span className={styles.backArrow}><IconArrow size={14} /></span>
          Back
        </button>
      </div>

      {/* ─── CAROUSEL ─── */}
      <div className={styles.carouselSection}>
        {images.length > 0 ? (
          <Carousel images={images} title={trip.title} />
        ) : (
          <div className={styles.noImage}>No images available</div>
        )}
      </div>

      {/* ─── TRIP DETAILS ─── */}
      <div className={styles.content}>
        <div className={styles.contentInner}>

          {/* Header */}
          <div className={styles.header}>
            <div className={styles.badges}>
              <span className={styles.catBadge} style={{ background: cat.bg, color: cat.color }}>
                {trip.category}
              </span>
              {trip.thisWeek && (
                <span className={styles.weekBadge}>🗓 This Week</span>
              )}
            </div>
            <h1 className={styles.title}>{trip.title}</h1>
            {trip.titleAr && <p className={styles.titleAr}>{trip.titleAr}</p>}
          </div>

          {/* Info Grid */}
          <div className={styles.infoGrid}>
            <div className={styles.infoCard}>
              <span className={styles.infoIcon}><IconMapPin size={18} /></span>
              <div>
                <div className={styles.infoLabel}>Location</div>
                <div className={styles.infoValue}>{trip.location}</div>
              </div>
            </div>
            <div className={styles.infoCard}>
              <span className={styles.infoIcon}>📅</span>
              <div>
                <div className={styles.infoLabel}>Date</div>
                <div className={styles.infoValue}>{trip.date}</div>
              </div>
            </div>
            <div className={styles.infoCard}>
              <span className={styles.infoIcon}><IconClock size={18} /></span>
              <div>
                <div className={styles.infoLabel}>Meeting Time</div>
                <div className={styles.infoValue}>{trip.time}</div>
              </div>
            </div>
            <div className={styles.infoCard}>
              <span className={styles.infoIcon}>📏</span>
              <div>
                <div className={styles.infoLabel}>Distance</div>
                <div className={styles.infoValue}>{trip.distance}</div>
              </div>
            </div>
            <div className={styles.infoCard}>
              <span className={styles.infoIcon}>🥾</span>
              <div>
                <div className={styles.infoLabel}>Difficulty</div>
                <div className={styles.infoValue}>{trip.level}</div>
              </div>
            </div>
            <div className={styles.infoCard}>
              <span className={styles.infoIcon}>💰</span>
              <div>
                <div className={styles.infoLabel}>Fees</div>
                <div className={styles.infoValue}>{trip.fees}</div>
              </div>
            </div>
          </div>

          {/* Description */}
          {trip.description && (
            <div className={styles.descSection}>
              <h2 className={styles.descTitle}>About this trip</h2>
              <p className={styles.desc}>{trip.description}</p>
            </div>
          )}

          {/* Highlights */}
          {trip.highlights && trip.highlights.length > 0 && (
            <div className={styles.highlightsSection}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.sectionTitleIcon}>✦</span> Highlights
              </h2>
              <ul className={styles.highlightsList}>
                {trip.highlights.map((h, i) => (
                  <li key={i} className={styles.highlightItem}>
                    <span className={styles.highlightDot} />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Included */}
          {trip.included && (
            <div className={styles.includedSection}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.sectionTitleIcon}>✦</span> Included
              </h2>
              <p className={styles.includedText}>{trip.included}</p>
            </div>
          )}

          {/* CTA */}
          <div className={styles.cta}>
            <a
              href={`https://wa.me/96279000000?text=I'm interested in: ${encodeURIComponent(trip.title)}`}
              target="_blank"
              rel="noreferrer"
              className={styles.ctaBtn}
            >
              Book this Trip <IconArrow size={16} />
            </a>
            <button className={styles.allTripsBtn} onClick={() => navigate('/trips')}>
              View All Trips
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
    </>
  );
}

